import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lightbulb, Award, Leaf, Users, Target, Eye, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { coreValues } from '../mockData';

const iconMap = { Shield, Lightbulb, Award, Leaf, Users };

const ChapterHeading = ({ number, title }) => (
  <div className="flex items-end space-x-4 mb-8">
    <span className="text-6xl md:text-7xl font-extrabold text-outline leading-none select-none">{number}</span>
    <h2 className="text-3xl md:text-4xl font-bold text-charcoal pb-1">{title}</h2>
  </div>
);

const whyChoose = [
  {
    title: 'Diversified Expertise',
    text: 'A multi-sector approach that allows us to bring broad perspective and integrated solutions to every engagement.'
  },
  {
    title: 'Value-Driven Delivery',
    text: 'Every project is guided by our commitment to driving value and adding solutions for clients and communities.'
  },
  {
    title: 'Quality Without Compromise',
    text: 'We hold ourselves to high standards of workmanship and professionalism across all our service lines.'
  },
  {
    title: 'Community-Minded Growth',
    text: 'We believe sustainable development means growing alongside the communities in which we operate.'
  }
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="A Diversified Company,"
        accent="United by Purpose"
        description="Alakhe Conglomerate is a diversified company focused on delivering value-driven solutions across multiple sectors."
      />

      {/* 01 — Who We Are */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <ChapterHeading number="01" title="Who We Are" />
              <p className="text-gray-600 leading-relaxed mb-4">
                Alakhe Conglomerate (Pty) Ltd is a diversified South African company operating across
                construction, infrastructure, property development, and industrial sectors. We combine
                professional expertise with a genuine commitment to the communities we serve.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From our base in Reitz, Free State, we work with businesses, communities, and public
                entities to deliver reliable, sustainable, and value-driven solutions — always guided
                by our promise: Driving Value, Adding Solutions.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                  alt="Alakhe Conglomerate"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 02 & 03 — Mission and Vision */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal>
              <div className="bg-white rounded-2xl p-10 border border-gray-100 shadow-md h-full" data-testid="mission-card">
                <ChapterHeading number="02" title="Our Mission" />
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-brand-red" />
                </div>
                <p className="text-gray-600 leading-relaxed">
                  To deliver innovative, reliable, and sustainable solutions across multiple industries,
                  helping businesses and communities grow through quality, integrity, and excellence in
                  everything we do.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-charcoal rounded-2xl p-10 shadow-md h-full" data-testid="vision-card">
                <div className="flex items-end space-x-4 mb-8">
                  <span className="text-6xl md:text-7xl font-extrabold leading-none select-none" style={{ color: 'transparent', WebkitTextStroke: '1.5px #6b6b6b' }}>03</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-white pb-1">Our Vision</h2>
                </div>
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-brand-red" />
                </div>
                <p className="text-gray-300 leading-relaxed">
                  To be a recognized leader known for excellence, innovation, and dedication to sustainable
                  development — a company whose work leaves a lasting, positive impact on the communities
                  and industries we serve.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 04 — Our Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <Reveal>
            <ChapterHeading number="04" title="Our Values" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-4">
            {coreValues.map((value, index) => {
              const Icon = iconMap[value.icon];
              return (
                <Reveal key={value.id} delay={index * 0.08}>
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-brand-red hover:shadow-lg transition-all duration-300 text-center group h-full">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-red transition-colors">
                      <Icon className="w-8 h-8 text-brand-red group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="text-lg font-bold text-charcoal mb-2">{value.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 05 — Why Choose */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <Reveal>
            <ChapterHeading number="05" title="Why Choose Alakhe Conglomerate" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
            {whyChoose.map((item, index) => (
              <Reveal key={item.title} delay={index * 0.1}>
                <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-start space-x-4 h-full">
                  <CheckCircle2 className="w-7 h-7 text-brand-red flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xl font-bold text-charcoal mb-2">{item.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="text-center mt-16">
            <Button
              onClick={() => navigate('/contact')}
              data-testid="about-cta-button"
              className="bg-brand-red hover:bg-red-700 text-white font-semibold text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all group"
            >
              Work With Us
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
